import type { Difficulty } from '../sudoku/types';
import type { GameSnapshot } from './state';

/**
 * Persistenz über localStorage. Alle Zugriffe sind gekapselt und fehlertolerant –
 * in der TWA oder im Privatmodus kann Speichern fehlschlagen, das darf das Spiel
 * nicht abbrechen.
 */

const SAVE_KEY = 'sudoku.save.v1';
const STATS_KEY = 'sudoku.stats.v1';
const SETTINGS_KEY = 'sudoku.settings.v1';

function read<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* Speicher nicht verfügbar – Spiel läuft trotzdem weiter. */
  }
}

// -- Laufende Partie --------------------------------------------------------

export function saveGame(snapshot: GameSnapshot): void {
  write(SAVE_KEY, snapshot);
}

export function loadGame(): GameSnapshot | null {
  const snapshot = read<GameSnapshot>(SAVE_KEY);
  if (!snapshot || !Array.isArray(snapshot.values) || snapshot.values.length !== 81) return null;
  if (!Array.isArray(snapshot.solution) || snapshot.solution.length !== 81) return null;
  return snapshot;
}

export function clearGame(): void {
  try {
    localStorage.removeItem(SAVE_KEY);
  } catch {
    /* ignorieren */
  }
}

// -- Statistik --------------------------------------------------------------

export interface DifficultyStats {
  started: number;
  won: number;
  bestMs: number | null;
  totalMs: number;
  /** Partien ohne Fehler und ohne Tipp. */
  flawless: number;
}

export interface Stats {
  perDifficulty: Record<Difficulty, DifficultyStats>;
  currentStreak: number;
  bestStreak: number;
}

function emptyDifficultyStats(): DifficultyStats {
  return { started: 0, won: 0, bestMs: null, totalMs: 0, flawless: 0 };
}

export function emptyStats(): Stats {
  return {
    perDifficulty: {
      easy: emptyDifficultyStats(),
      medium: emptyDifficultyStats(),
      hard: emptyDifficultyStats(),
      expert: emptyDifficultyStats(),
    },
    currentStreak: 0,
    bestStreak: 0,
  };
}

export function loadStats(): Stats {
  const stored = read<Partial<Stats>>(STATS_KEY);
  const stats = emptyStats();
  if (!stored?.perDifficulty) return stats;
  for (const key of Object.keys(stats.perDifficulty) as Difficulty[]) {
    stats.perDifficulty[key] = { ...stats.perDifficulty[key], ...stored.perDifficulty[key] };
  }
  stats.currentStreak = stored.currentStreak ?? 0;
  stats.bestStreak = stored.bestStreak ?? 0;
  return stats;
}

export function saveStats(stats: Stats): void {
  write(STATS_KEY, stats);
}

export function recordStart(difficulty: Difficulty): Stats {
  const stats = loadStats();
  stats.perDifficulty[difficulty].started++;
  saveStats(stats);
  return stats;
}

export function recordWin(
  difficulty: Difficulty,
  elapsedMs: number,
  mistakes: number,
  hintsUsed: number,
): Stats {
  const stats = loadStats();
  const entry = stats.perDifficulty[difficulty];
  entry.won++;
  entry.totalMs += elapsedMs;
  if (entry.bestMs === null || elapsedMs < entry.bestMs) entry.bestMs = elapsedMs;
  if (mistakes === 0 && hintsUsed === 0) entry.flawless++;
  stats.currentStreak++;
  stats.bestStreak = Math.max(stats.bestStreak, stats.currentStreak);
  saveStats(stats);
  return stats;
}

export function resetStats(): Stats {
  const stats = emptyStats();
  saveStats(stats);
  return stats;
}

// -- Einstellungen ----------------------------------------------------------

export type ThemeSetting = 'system' | 'light' | 'dark';

export interface Settings {
  theme: ThemeSetting;
  /** Konflikte in Zeile/Spalte/Block rot markieren. */
  highlightConflicts: boolean;
  /** Gleiche Ziffern im ganzen Gitter hervorheben. */
  highlightSameDigit: boolean;
  /** Notizen automatisch anpassen, wenn eine Ziffer gesetzt wird. */
  autoCleanNotes: boolean;
  /** Falsche Eingaben sofort markieren. */
  checkMistakes: boolean;
  /** Vibration bei Fehleingabe. */
  haptics: boolean;
  /** Timer anzeigen. */
  showTimer: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  theme: 'system',
  highlightConflicts: true,
  highlightSameDigit: true,
  autoCleanNotes: true,
  checkMistakes: true,
  haptics: true,
  showTimer: true,
};

export function loadSettings(): Settings {
  return { ...DEFAULT_SETTINGS, ...(read<Partial<Settings>>(SETTINGS_KEY) ?? {}) };
}

export function saveSettings(settings: Settings): void {
  write(SETTINGS_KEY, settings);
}
